import React, { useState } from 'react';
import {
  Card,
  CardContent,
  CardActions,
  Box,
  Typography,
  Avatar,
  Chip,
  IconButton,
  Tooltip,
  Collapse,
  Divider,
} from '@mui/material';
import {
  FavoriteBorder as FavoriteIcon,
  Favorite as FavoriteFilledIcon,
  ChatBubbleOutline as CommentIcon,
  Share as ShareIcon,
  MoreVert as MoreIcon,
  Person as PersonIcon,
  Lock as LockIcon,
  Public as PublicIcon,
  AccessTime as TimeIcon,
} from '@mui/icons-material';
import { getCategoryColor } from '../../theme/theme';
import ReactionBar from './ReactionBar';

// Helper function to format relative time
const formatRelativeTime = (dateString) => {
  if (!dateString) return '';

  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);
  const diffWeeks = Math.floor(diffDays / 7);
  const diffMonths = Math.floor(diffDays / 30);

  if (diffSeconds < 60) return 'Just now';
  if (diffMinutes < 60) return `${diffMinutes}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  if (diffWeeks < 4) return `${diffWeeks}w ago`;
  if (diffMonths < 12) return `${diffMonths}mo ago`;
  return date.toLocaleDateString();
};

// Get initials from username
const getInitials = (name) => {
  if (!name || name === 'Anonymous') return '?';
  return name
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

function PrayerCard({
  prayer,
  onCollect,
  onComment,
  onShare,
  onReact,
  onMenuClick,
  collected = false,
  showCircle = false,
  variant = 'default', // 'default', 'compact', 'detailed'
  elevation = 0,
}) {
  const [isHovered, setIsHovered] = useState(false);
  const [showFullContent, setShowFullContent] = useState(false);

  const {
    id,
    content,
    category = 'General',
    created_at,
    is_anonymous,
    username,
    user_id,
    circle_name,
    circle_id,
    collection_count = 0,
    comment_count = 0,
    reactions = {},
    user_reaction = null,
  } = prayer;

  const displayName = is_anonymous ? 'Anonymous' : (username || 'Unknown');
  const categoryColor = getCategoryColor(category);
  const isLongContent = content && content.length > 200;

  const handleCollectClick = (e) => {
    e.stopPropagation();
    if (onCollect) onCollect(prayer);
  };

  const handleCommentClick = (e) => {
    e.stopPropagation();
    if (onComment) onComment(prayer);
  };

  const handleShareClick = (e) => {
    e.stopPropagation();
    if (onShare) onShare(prayer);
  };

  const handleMenuClick = (e) => {
    e.stopPropagation();
    if (onMenuClick) onMenuClick(e, prayer);
  };

  const handleReact = (reactionType) => {
    if (onReact) onReact(prayer, reactionType);
  };

  return (
    <Card
      elevation={isHovered ? 3 : elevation}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      sx={{
        position: 'relative',
        overflow: 'visible',
        transition: 'all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
        '&:hover': {
          transform: variant === 'compact' ? 'none' : 'translateY(-2px)',
        },
      }}
    >
      {/* Category indicator strip */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 4,
          backgroundColor: categoryColor,
          borderRadius: '20px 20px 0 0',
          opacity: 0.8,
        }}
      />

      <CardContent sx={{ pt: 3 }}>
        {/* Header: Avatar + Name + Time + Menu */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
          <Avatar
            sx={{
              width: variant === 'compact' ? 36 : 44,
              height: variant === 'compact' ? 36 : 44,
              bgcolor: is_anonymous ? 'grey.400' : 'primary.main',
              fontSize: variant === 'compact' ? '0.875rem' : '1rem',
              mr: 1.5,
            }}
          >
            {is_anonymous ? <PersonIcon /> : getInitials(displayName)}
          </Avatar>

          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                color: 'text.primary',
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
              }}
            >
              {displayName}
              {is_anonymous && (
                <Tooltip title="Posted anonymously">
                  <LockIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                </Tooltip>
              )}
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                }}
              >
                <TimeIcon sx={{ fontSize: 12 }} />
                {formatRelativeTime(created_at)}
              </Typography>

              {showCircle && circle_name && (
                <Chip
                  size="small"
                  label={circle_name}
                  icon={circle_id ? <LockIcon /> : <PublicIcon />}
                  sx={{
                    height: 20,
                    fontSize: '0.7rem',
                    '& .MuiChip-icon': { fontSize: 12 },
                  }}
                />
              )}
            </Box>
          </Box>

          {/* Category chip */}
          <Chip
            label={category}
            size="small"
            sx={{
              bgcolor: `${categoryColor}20`,
              color: categoryColor,
              fontWeight: 500,
              fontSize: '0.75rem',
              height: 24,
              mr: onMenuClick ? 0.5 : 0,
            }}
          />

          {onMenuClick && (
            <IconButton
              size="small"
              onClick={handleMenuClick}
              sx={{ ml: 0.5 }}
              aria-label="More options"
            >
              <MoreIcon fontSize="small" />
            </IconButton>
          )}
        </Box>

        {/* Prayer Content */}
        <Box sx={{ mb: 1 }}>
          <Typography
            variant="body1"
            sx={{
              fontFamily: '"Playfair Display", Georgia, serif',
              fontStyle: 'italic',
              color: 'text.primary',
              lineHeight: 1.8,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              ...(isLongContent && !showFullContent && {
                display: '-webkit-box',
                WebkitLineClamp: 4,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }),
            }}
          >
            {content}
          </Typography>

          {isLongContent && (
            <Typography
              component="span"
              variant="body2"
              onClick={() => setShowFullContent(!showFullContent)}
              sx={{
                color: 'primary.main',
                cursor: 'pointer',
                fontWeight: 500,
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              {showFullContent ? 'Show less' : 'Read more'}
            </Typography>
          )}
        </Box>
      </CardContent>

      {/* Actions */}
      {(onReact || onCollect || onComment || onShare) && (
        <CardActions
          sx={{
            px: 2,
            pb: 2,
            pt: 0,
            flexDirection: 'column',
            alignItems: 'stretch',
            gap: 1,
          }}
        >
          {/* Reactions row */}
          {onReact && (
            <Box sx={{ mb: 0.5 }}>
              <ReactionBar
                reactions={reactions}
                userReaction={user_reaction}
                onReact={handleReact}
                compact={variant === 'compact'}
              />
            </Box>
          )}

          {/* Action buttons row */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              borderTop: onReact ? 1 : 0,
              borderColor: 'divider',
              pt: onReact ? 1 : 0,
            }}
          >
            {onCollect && (
              <Tooltip title={collected ? 'Collected' : 'Collect this prayer'}>
                <IconButton
                  size="small"
                  onClick={handleCollectClick}
                  sx={{
                    color: collected ? 'error.main' : 'text.secondary',
                    '&:hover': {
                      color: 'error.main',
                      bgcolor: 'rgba(229, 62, 62, 0.1)',
                    },
                  }}
                  aria-label={collected ? 'Collected' : 'Collect prayer'}
                >
                  {collected ? (
                    <FavoriteFilledIcon fontSize="small" />
                  ) : (
                    <FavoriteIcon fontSize="small" />
                  )}
                </IconButton>
              </Tooltip>
            )}
            {collection_count > 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
                {collection_count}
              </Typography>
            )}

            {onComment && (
              <>
                <Tooltip title="Comments">
                  <IconButton
                    size="small"
                    onClick={handleCommentClick}
                    sx={{
                      color: 'text.secondary',
                      '&:hover': {
                        color: 'primary.main',
                        bgcolor: 'rgba(44, 82, 130, 0.1)',
                      },
                    }}
                    aria-label="View comments"
                  >
                    <CommentIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                {comment_count > 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
                    {comment_count}
                  </Typography>
                )}
              </>
            )}

            {onShare && (
              <Tooltip title="Share">
                <IconButton
                  size="small"
                  onClick={handleShareClick}
                  sx={{
                    color: 'text.secondary',
                    '&:hover': {
                      color: 'secondary.main',
                      bgcolor: 'rgba(212, 165, 116, 0.1)',
                    },
                  }}
                  aria-label="Share prayer"
                >
                  <ShareIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </CardActions>
      )}
    </Card>
  );
}

export default PrayerCard;
